import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollReveal from './ScrollReveal';
import { Building2, Users } from 'lucide-react';
import b2bImage from '@/assets/b2b.png';
import b2cImage from '@/assets/b2c.png';

const TargetSection = () => {
  const [activeTab, setActiveTab] = useState<'b2b' | 'b2c'>('b2b');

  const services = {
    b2b: [
      'Lead Generation Qualificata',
      'LinkedIn Marketing',
      'Content Marketing B2B',
      'Account Based Marketing',
      'Sales Funnel Automation',
    ],
    b2c: [
      'E-commerce Growth',
      'Social Media Marketing',
      'Influencer Partnership',
      'Customer Retention',
      'Brand Awareness',
    ],
  };

  return (
    <section id="servizi" className="py-16 md:py-20 relative overflow-hidden bg-navy/30">
      <div className="container-premium">
        <div className="text-center mb-12">
          <ScrollReveal>
            <p className="text-gold tracking-[0.2em] uppercase text-sm mb-6">
              Per chi lavoriamo
            </p>
          </ScrollReveal>
          
          <ScrollReveal delay={0.1}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-6">
              Che tipo di azienda sei?
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={0.2}>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Strategie personalizzate per ogni modello di business
            </p>
          </ScrollReveal>
        </div>

        {/* Tab Selector */}
        <ScrollReveal delay={0.3}>
          <div className="flex justify-center mb-10">
            <div className="glass-card p-1.5 sm:p-2 flex gap-1 sm:gap-2 w-full sm:w-auto">
              <button
                onClick={() => setActiveTab('b2b')}
                className={`flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-8 py-3 sm:py-4 rounded-xl font-medium transition-all duration-300 text-sm sm:text-base flex-1 sm:flex-auto ${
                  activeTab === 'b2b'
                    ? 'bg-gold text-navy-deep'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="hidden sm:inline whitespace-nowrap">Business to Business</span>
                <span className="sm:hidden whitespace-nowrap">B2B</span>
              </button>
              <button
                onClick={() => setActiveTab('b2c')}
                className={`flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-8 py-3 sm:py-4 rounded-xl font-medium transition-all duration-300 text-sm sm:text-base flex-1 sm:flex-auto ${
                  activeTab === 'b2c'
                    ? 'bg-gold text-navy-deep'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <span className="hidden sm:inline whitespace-nowrap">Business to Consumer</span>
                <span className="sm:hidden whitespace-nowrap">B2C</span>
              </button>
            </div>
          </div>
        </ScrollReveal>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              <div className="glass-card-hover p-8 md:p-10">
                <h3 className="font-serif text-2xl md:text-3xl mb-6">
                  {activeTab === 'b2b' 
                    ? 'Aziende che vendono ad altre aziende' 
                    : 'Aziende che vendono al consumatore finale'}
                </h3>
                <p className="text-muted-foreground mb-8">
                  {activeTab === 'b2b'
                    ? 'Cicli di vendita lunghi, decisioni complesse, relazioni di valore. Sappiamo come raggiungerti e convertirti.'
                    : 'Acquisti emotivi, brand experience, fidelizzazione. Creiamo connessioni che portano vendite.'}
                </p>
                <ul className="space-y-4">
                  {services[activeTab].map((service, index) => (
                    <li 
                      key={service}
                      className="flex items-center gap-3 text-foreground"
                    >
                      <span className="w-2 h-2 rounded-full bg-gold" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative h-80 md:h-auto min-h-[320px] glass-card overflow-hidden rounded-2xl">
                <img 
                   src={activeTab === 'b2b' ? b2bImage : b2cImage}
                   alt={activeTab === 'b2b' ? 'Business to Business' : 'Business to Consumer'}
                   width={530}
                   height={466}
                   loading="lazy"
                   className="absolute inset-0 w-full h-full object-cover"
                 />
                <div className="absolute inset-0 bg-gradient-to-t from-background/40 to-transparent" />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* CTA */}
        <ScrollReveal delay={0.4}>
          <div className="text-center mt-12">
            <a href="/contatti" className="btn-premium px-14 py-5 text-base">
              <span>Scopri i Nostri Servizi per Te</span>
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default TargetSection;
